import React, { useEffect, useCallback, useState } from 'react';
import { 
  Box, 
  Switch, 
  Typography, 
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputAdornment,
  Divider,
  Button,
  Snackbar,
  Alert,
  CircularProgress
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import TimeIntervalInput from '../shared/TimeIntervalInput';
import { useDashboardData } from '../../contexts/DashboardDataContext';
import debounce from 'lodash.debounce';

const DashboardScheduler = () => {
  const {
    scheduler = { enabled: false, rules: [] },
    rotation = { enabled: false, interval: 300 },
    setSchedulerEnabled,
    setSchedulerRules,
    setSchedulerRotation,
    zones = [],
    fetchSchedulerConfig,
    isDashboardActive
  } = useDashboardData();

  // Make sure rules is always an array
  const rules = Array.isArray(scheduler.rules) ? scheduler.rules : [];

  // Add state for initial loading
  const [isLoading, setIsLoading] = useState(true);

  // Update loadInitialConfig to use scheduler_check instead of scheduler_load
  const loadInitialConfig = useCallback(async () => {
    try {
      // First check localStorage for saved interval
      const savedInterval = Number(localStorage.getItem('dashboardRotationInterval'));
      
      // Use the saved interval from localStorage if available
      if (!isNaN(savedInterval) && savedInterval > 0) {
        console.log('[DashboardScheduler] Using interval from localStorage:', savedInterval, 
                    '(', Math.floor(savedInterval/60), 'minutes )');
        
        // Initialize pendingInterval with the saved localStorage value
        setPendingInterval(savedInterval);
        
        // Also update the rotation context with the localStorage value
        // This ensures the UI and context stay in sync
        setSchedulerRotation(prev => ({
          ...prev,
          interval: savedInterval
        }));
      } else {
        console.log('[DashboardScheduler] No saved interval in localStorage, using default');
      }
      
      // Get config from context first
      if (scheduler?.data) {
        console.log('[DashboardScheduler] Using context data:', scheduler.data);
        if (scheduler.data.rotation) {
          const { enabled, interval } = scheduler.data.rotation;
          // Only update pendingInterval if we don't have a saved value
          if (savedInterval === 0 || isNaN(savedInterval)) {
            setPendingInterval(interval);
          }
        }
        setIsLoading(false);
        return;
      }

      // Otherwise fetch from backend
      const token = document.cookie
        .split('; ')
        .find(row => row.startsWith('token='))
        ?.split('=')[1];

      if (!token) {
        throw new Error('No token found');
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/dashboard/scheduler/s-check`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch scheduler config');
      }

      const data = await response.json();
      console.log('[DashboardScheduler] Loaded config:', data);
        
      // Update rotation settings via context
      if (data.rotation) {
        // Only use server interval if we don't have one from localStorage
        const actualInterval = savedInterval || Number(data.rotation.interval) || 300;
        await setSchedulerRotation({
          enabled: Boolean(data.rotation.enabled),
          interval: actualInterval
        });
        
        // Make sure we update pendingInterval with the correct value
        console.log('[DashboardScheduler] Setting interval:', actualInterval, 
                    '(', Math.floor(actualInterval/60), 'minutes )');
        
        // Only update pendingInterval if we don't have a localStorage value
        if (savedInterval === 0 || isNaN(savedInterval)) {
          setPendingInterval(actualInterval);
        }
        
        // Always store in localStorage as backup
        localStorage.setItem('dashboardRotationInterval', String(actualInterval));
      }

      // Update scheduler settings via context
      if (data.scheduler) {
        setSchedulerEnabled(Boolean(data.scheduler.enabled));
        setSchedulerRules(data.scheduler.rules || []);
      }

      // Store hash for future checks
      localStorage.setItem('lastSchedulerCheck', data.hash);

    } catch (error) {
      console.error('[DashboardScheduler] Error loading initial config:', error);
    } finally {
      setIsLoading(false);
    }
  }, [scheduler?.data, setSchedulerRotation, setSchedulerEnabled, setSchedulerRules]);

  // Load initial config when component mounts
  useEffect(() => {
    loadInitialConfig();
  }, [loadInitialConfig]);

  // Update saveSchedulerConfig to handle responses better
  const saveSchedulerConfig = async () => {
    try {
      console.log('[DashboardScheduler] Saving scheduler config...');
      const token = document.cookie
        .split('; ')
        .find(row => row.startsWith('token='))
        ?.split('=')[1];

      if (!token) return;

      const config = {
        dashboard_config: {
          scheduler: {
            enabled: scheduler.enabled,
            rules: scheduler.rules || []
          },
          rotation: {
            enabled: rotation.enabled,
            interval: Math.max(60, rotation.interval)
          }
        }
      };

      console.log('[DashboardScheduler] Sending config:', JSON.stringify(config));

      const response = await fetch(`${process.env.REACT_APP_API_URL}/dashboard/scheduler/s-save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'Cache-Control': 'no-cache'
        },
        body: JSON.stringify(config)
      });

      if (!response.ok) throw new Error('Failed to save scheduler config');
      
      const result = await response.json();
      console.log('[DashboardScheduler] Save response:', result);
      
      // Make sure we don't lose our current state if the server response is missing data
      if (result.config) {
        const savedConfig = result.config;
        if (savedConfig.rotation?.enabled !== undefined) {
          setSchedulerRotation(prev => ({
            ...prev,
            ...savedConfig.rotation
          }));
        }
        if (savedConfig.scheduler?.enabled !== undefined) {
          setSchedulerEnabled(savedConfig.scheduler.enabled);
        }
        if (savedConfig.scheduler?.rules) {
          setSchedulerRules(savedConfig.scheduler.rules);
        }
      }
      
    } catch (error) {
      console.error('[DashboardScheduler] Error saving config:', error);
    }
  };

  // Modify checkSchedulerChanges to use the config from scheduler_check directly
  const checkSchedulerChanges = useCallback(async () => {
    try {
      const token = document.cookie
        .split('; ')
        .find(row => row.startsWith('token='))
        ?.split('=')[1];

      if (!token) return;

      const response = await fetch(`${process.env.REACT_APP_API_URL}/dashboard/scheduler/s-check`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        }
      });

      if (response.ok) {
        const data = await response.json();
        const lastHash = localStorage.getItem('lastSchedulerCheck');
        
        // Only update if hash has changed
        if (data.hash && data.hash !== lastHash) {
          console.log('[DashboardScheduler] Config changed, applying new settings');
          
          if (data.rotation) {
            setSchedulerRotation({
              enabled: Boolean(data.rotation.enabled),
              interval: Number(data.rotation.interval) || 300
            });
          }
          
          if (data.scheduler) {
            setSchedulerEnabled(Boolean(data.scheduler.enabled));
            setSchedulerRules(data.scheduler.rules || []);
          }
          
          localStorage.setItem('lastSchedulerCheck', data.hash);
        }
      }
    } catch (error) {
      console.error('[DashboardScheduler] Error checking config:', error);
    }
  }, [setSchedulerRotation, setSchedulerEnabled, setSchedulerRules]);

  // Only check for changes when component mounts
  useEffect(() => {
    if (!isDashboardActive) return;
    checkSchedulerChanges();
  }, [isDashboardActive, checkSchedulerChanges]); // Added checkSchedulerChanges to deps
  
  // Debug log rotation state changes
  useEffect(() => {
    console.log('[DashboardScheduler] Rotation state changed:', rotation?.enabled);
  }, [rotation?.enabled]);

  const handleAddRule = () => {
    if (!zones || zones.length === 0) return;
    
    const newRule = {
      id: Date.now(),
      time: '12:00',
      zoneId: zones[0].id,
      zoneName: zones[0].name
    };
    // Use rules array from state
    setSchedulerRules([...scheduler.rules, newRule]);
  };

  const handleUpdateRule = (ruleId, field, value) => {
    // Use rules array from state
    setSchedulerRules(scheduler.rules.map(rule => {
      if (rule.id === ruleId) {
        if (field === 'zoneId') {
          const selectedZone = zones.find(z => z.id === value);
          return {
            ...rule,
            zoneId: value,
            zoneName: selectedZone.name
          };
        }
        return { ...rule, [field]: value };
      }
      return rule;
    }));
  };

  const handleDeleteRule = (ruleId) => {
    // Use rules array from state
    setSchedulerRules(scheduler.rules.filter(rule => rule.id !== ruleId));
  };

  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  // Initialize pendingInterval from rotation.interval
  const [pendingInterval, setPendingInterval] = useState(rotation?.interval || 300);

  // Log when rotation.interval changes to help debug
  useEffect(() => {
    console.log('[DashboardScheduler] rotation.interval changed:', rotation?.interval);
    
    // If rotation.interval changes and it's different from pendingInterval,
    // update pendingInterval to match the new value (when not being edited)
    if (rotation?.interval && rotation.interval !== pendingInterval) {
      console.log('[DashboardScheduler] Updating pendingInterval to match rotation.interval:', rotation.interval);
      setPendingInterval(rotation.interval);
    }
  }, [rotation?.interval, pendingInterval]);

  const handleIntervalChange = ({ hours, minutes }) => {
    const intervalInSeconds = Math.max(60, (hours * 3600) + (minutes * 60));
    console.log('[DashboardScheduler] New interval:', intervalInSeconds, 'seconds');
    setPendingInterval(intervalInSeconds);
  };

  const handleSaveInterval = async () => {
    if (pendingInterval) {
      console.log('[DashboardScheduler] Saving interval:', pendingInterval);
      
      // Update local state with pending interval
      const newRotation = {
        ...rotation,
        interval: pendingInterval // Use pending interval directly, already validated
      };

      // Update the rotation in context and save to localStorage
      setSchedulerRotation(newRotation);
      localStorage.setItem('dashboardRotationInterval', String(pendingInterval));
      
      // Create config with new interval
      const config = {
        dashboard_config: {
          scheduler: {
            enabled: scheduler.enabled,
            rules: scheduler.rules || []
          },
          rotation: {
            enabled: rotation.enabled,
            interval: pendingInterval // Use pending interval here too
          }
        }
      };

      // Save config to server
      try {
        const token = document.cookie
          .split('; ')
          .find(row => row.startsWith('token='))
          ?.split('=')[1];

        if (!token) return;

        // Oppdater til å bruke scheduler_save endepunktet
        const response = await fetch(`${process.env.REACT_APP_API_URL}/dashboard/scheduler/s-save`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            'Cache-Control': 'no-cache'
          },
          body: JSON.stringify(config)
        });

        if (!response.ok) throw new Error('Failed to save scheduler config');
        
        // Clear pending state and show success message only after successful save
        setPendingInterval(null);
        setShowSaveSuccess(true);
        
      } catch (error) {
        console.error('[DashboardScheduler] Error saving interval:', error);
        // Don't clear pending state if save failed
      }
    }
  };

  // Add this function to handle toggle state changes
  const [errorMessage, setErrorMessage] = useState(null);

  // Debounced rotation toggle handler
  const debouncedRotationToggle = useCallback(
    debounce(async (enabled) => {
      if (rotation?.isSaving) return;
      setErrorMessage(null);
      const currentInterval = pendingInterval !== null ? pendingInterval : rotation?.interval || 300;
      
      try {
        await setSchedulerRotation({
          enabled,
          interval: currentInterval
        });
        
        if (pendingInterval !== null) {
          setPendingInterval(null);
        }
      } catch (error) {
        console.error('[DashboardScheduler] Failed to toggle rotation:', error);
        setErrorMessage('Failed to save rotation settings. Please try again.');
        setTimeout(() => setErrorMessage(null), 5000);
      }
    }, 1000, { leading: true, trailing: false }),
    [rotation?.isSaving, pendingInterval, setSchedulerRotation, setErrorMessage]
  );

  const handleRotationToggle = (enabled) => {
    // Prevent toggle if already saving
    if (rotation?.isSaving) return;
    
    // Call the debounced toggle handler
    debouncedRotationToggle(enabled);
  };

  // Convert seconds to hours and minutes for the TimeIntervalInput
  const intervalToHoursMinutes = (seconds) => {
    const totalSeconds = Number(seconds) || 300; // Default to 300 seconds (5 minutes) if invalid
    return {
      hours: Math.floor(totalSeconds / 3600),
      minutes: Math.floor((totalSeconds % 3600) / 60)
    };
  };

  return (
    isLoading ? (
      <Paper sx={{ p: 2, bgcolor: '#1e1e1e', color: 'white' }}>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Typography>Loading scheduler settings...</Typography>
        </Box>
      </Paper>
    ) : (
      <Paper sx={{ p: 2, bgcolor: '#1e1e1e', color: 'white' }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {/* Scheduler Settings */}
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
                Auto-switch zones at scheduled times
              </Typography>
              <Switch
                checked={Boolean(scheduler.enabled)}
                onChange={(e) => setSchedulerEnabled(e.target.checked)}
              />
            </Box>

            {scheduler.enabled && (
              <>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ color: '#aaa' }}>Time</TableCell>
                        <TableCell sx={{ color: '#aaa' }}>Switch to Zone</TableCell>
                        <TableCell sx={{ color: '#aaa', width: 50 }}></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {scheduler.rules.map((rule) => (
                        <TableRow key={rule.id}>
                          <TableCell sx={{ color: 'white' }}>
                            <TextField
                              type="time"
                              value={rule.time}
                              onChange={(e) => handleUpdateRule(rule.id, 'time', e.target.value)}
                              size="small"
                              sx={{
                                '& input': { color: 'white', p: '4px 8px' },
                                '& .MuiOutlinedInput-root': {
                                  '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.23)' },
                                  '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
                                }
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <FormControl fullWidth size="small">
                              <Select
                                value={rule.zoneId}
                                onChange={(e) => handleUpdateRule(rule.id, 'zoneId', e.target.value)}
                                sx={{
                                  color: 'white',
                                  '.MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'rgba(255, 255, 255, 0.23)'
                                  },
                                  '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'rgba(255, 255, 255, 0.5)'
                                  },
                                  '.MuiSvgIcon-root': {
                                    color: 'white'
                                  }
                                }}
                              >
                                {zones.map(zone => (
                                  <MenuItem key={zone.id} value={zone.id}>
                                    {zone.name}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </TableCell>
                          <TableCell>
                            <IconButton size="small" onClick={() => handleDeleteRule(rule.id)}>
                              <DeleteIcon sx={{ color: '#ff4444' }} />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Only show add button if we have zones */}
                {zones && zones.length > 0 && (
                  <Tooltip title="Add schedule rule">
                    <IconButton 
                      onClick={handleAddRule}
                      sx={{ mt: 2, color: '#4caf50' }}
                    >
                      <AddIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </>
            )}
          </Box>

          <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />

          {/* Rotation Settings */}
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
                  Auto-rotate through zones
                </Typography>
                <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                  {rotation?.isSaving && (
                    <CircularProgress
                      size={24}
                      thickness={6}
                      sx={{
                        position: 'absolute',
                        left: -28,
                        color: '#4a9bfc'
                      }}
                    />
                  )}
                  <Switch
                    checked={Boolean(rotation?.enabled)}
                    onChange={(e) => {
                      if (!rotation?.isSaving) {
                        const newValue = e.target.checked;
                        console.log('[DashboardScheduler] Switch state change:', newValue);
                        // Call the debounced handler
                        handleRotationToggle(newValue);
                      }
                    }}
                    disabled={rotation?.isSaving}
                    sx={{
                      pointerEvents: rotation?.isSaving ? 'none' : 'auto',
                      opacity: rotation?.isSaving ? 0.5 : 1,
                      '& .MuiSwitch-thumb': {
                        transition: 'all 0.2s'
                      },
                      '& .MuiSwitch-switchBase.Mui-disabled': {
                        opacity: 0.5
                      }
                    }}
                  />
                </Box>
              </Box>
            </Box>
            
            {rotation.enabled && (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                gap: 2,
                p: 2,
                bgcolor: 'rgba(255,255,255,0.05)', 
                borderRadius: 1
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="body2">Rotate every</Typography>
                  <TimeIntervalInput
                    value={intervalToHoursMinutes(pendingInterval ?? rotation.interval)}
                    onChange={handleIntervalChange}
                  />
                </Box>
                <Typography variant="body2" sx={{ color: '#aaaaaa', my: 1 }}>
                  Current setting: {Math.floor((pendingInterval ?? rotation.interval) / 60)} minutes
                </Typography>
                {pendingInterval !== null && pendingInterval !== rotation.interval && (
                  <Button 
                    variant="contained" 
                    color="primary"
                    onClick={handleSaveInterval}
                    sx={{ alignSelf: 'flex-end' }}
                  >
                    Save Interval ({Math.floor(pendingInterval / 60)} minutes)
                  </Button>
                )}
              </Box>
            )}
          </Box>
        </Box>

        {/* Add Snackbar for success message */}
        <Snackbar
          open={showSaveSuccess}
          autoHideDuration={3000}
          onClose={() => setShowSaveSuccess(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert 
            onClose={() => setShowSaveSuccess(false)} 
            severity="success"
            sx={{ width: '100%' }}
          >
            Rotation interval updated successfully
          </Alert>
        </Snackbar>
      </Paper>
    )
  );
};

export default DashboardScheduler;
